import React, { useState, useEffect, useRef, useContext } from "react";
import path from "path";
import fs from "fs";
import Image from "next/image";
import Head from "next/head";
import { Swiper, SwiperSlide } from "swiper/react";
import { useTranslation } from "@/components/useTranslation";
import Services from "@/components/static/services";
import PriceCard from "../../components/price/price-card";
import { MyContext } from "@/pages/_app";
import { useRouter } from "next/router";

const profit = [
  "0", "0", "0", "0", "0", "20", "40", "60", "80", "100",
  "130", "160", "190", "220", "250", "280", "310", "340",
  "370", "400", "450", "500", "550", "600", "650", "700",
  "750", "800", "850", "900",
];

const renderRangeInput = (category, currentValue, handleChange) => {
  const displayValue = currentValue + 1;
  const percentage = (currentValue / 29) * 100;

  return (
    <div className="custom-range-container relative h-[30px] bg-gray-200 rounded-full">
      <div
        className="range-thumb absolute top-0 w-[30px] h-[30px] bg-blue-600 rounded-full flex items-center justify-center text-white font-bold cursor-pointer"
        style={{ left: `calc(${percentage}% - 15px)` }}
      >
        {displayValue}
      </div>
      <input
        type="range"
        className="absolute opacity-0 w-full h-full top-0 left-0 m-0 p-0 cursor-pointer"
        min="0"
        max="29"
        step="1"
        value={currentValue}
        onChange={handleChange}
      />
    </div>
  );
};

function PlatformPage({ platform }) {
  const router = useRouter();
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [price, setPrice] = useState(platform.category === "Jameda Loschen" ? 350 : 300);
  const [proBewertung, setProBewertung] = useState(1);
  const [totalProfit, setTotalProfit] = useState(0);
  const [packages, setPackages] = useState({});
  const [value, setValue] = useState(0);
  const prevValueRef = useRef(1);

  const t = useTranslation();
  const {
    googleLoschenValue, setGoogleLoschenValue,
    jamedaLoschenValue, setJamedaLoschenValue,
    setIsSelected,
    setGlobalValue,
    setPriceLoschen,
    setSelectedPlatform,
  } = useContext(MyContext);

  const googleFirstPrice = 300;
  const jamedaFirstPrice = 350;

  useEffect(() => {
    const basePrice = platform.category === "Jameda Loschen" ? 350 : 300;
    setPrice(basePrice);
  }, [platform.category]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const getPackages = async () => {
      setPackages(t.data[0]);
    };
    getPackages();
  }, [t]);

  useEffect(() => {
    const handleRouteChange = () => {
      const basePrice = platform.category === "Jameda Loschen" ? 350 : 300;
      setPrice(basePrice);
      setProBewertung(1);
      setTotalProfit(0);
      setValue(0);
      setIsSelected(false);
      setGlobalValue(1);
      setGoogleLoschenValue(1);
      setJamedaLoschenValue(1);
      setSelectedPlatform(platform.category);
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [platform.category, router.events]);

  const handleChange = (event) => {
    const currentValue = parseInt(event.target.value);
    const basePrice = platform.category === "Jameda Loschen" ? jamedaFirstPrice : googleFirstPrice;
    const newPrice = basePrice * (currentValue + 1);
    setPrice(newPrice);
    setProBewertung(currentValue + 1);
    setTotalProfit(profit[currentValue]);
    setGlobalValue(currentValue + 1);
    setValue(currentValue);

    if (platform.category === "Google Loschen") {
      setGoogleLoschenValue(currentValue + 1);
      setSelectedPlatform("Google");
    } else {
      setJamedaLoschenValue(currentValue + 1);
      setSelectedPlatform("Jameda");
    }
    setIsSelected(true);
  };

  const renderHeroSection = () => {
    return (
      <section className="relative h-[91vh]">
        <Image
          width={1920}
          height={888}
          src={`/images/subpage/${platform.hero_background}`}
          alt={`${platform.category} Background Image`}
          className="absolute inset-0 object-cover w-full h-full brightness-50 md:brightness-75 lg:object-fill -z-10"
          priority
        />
        <div className="grid h-full max-w-screen-xl px-4 py-8 mx-auto lg:gap-8 xl:gap-0 lg:py-16 lg:grid-cols-12">
          <div className="max-w-md px-4 mr-auto place-self-center sm:px-10 md:px-0 lg:col-span-5">
            <h1 className="pt-5 text-3xl font-extrabold tracking-tight text-white lg:text-5xl">
              {
                platform.category.includes("Google Loschen") ? (
                  t.dynamic_page_loeschung_hero_title
                ) : platform.category.includes("Jameda") ? (
                  t.dynamic_page_loeschung_jameda_hero_title
                ) : (
                  `${t.positive} ${platform.category} ${t.buy_reviews}`
                )
              }

            </h1>
            <p className="mt-6 text-white">
              {
                platform.category.includes("Google Loschen") ? (
                  t.dynamic_page_loeschung_hero_description
                ) : platform.category.includes("Jameda") ? (
                  t.dynamic_page_loeschung_jameda_hero_description
                ) : (
                  t.dynamic_page_hero_description.replace(/@@burasidegisecek@@/g, platform.category)
                )
              }
            </p>
          </div>
          <div className="items-center justify-center hidden h-auto lg:mt-0 lg:col-span-7 lg:flex">
            <Image
              src={`/images/subpage/${platform.hero_images}`}
              height={800}
              width={800}
              priority
              alt={`${platform.category}`}
              className="h-auto w-auto max-h-[660px] rounded-3xl max-w-[514px]"
            />
          </div>
        </div>
        <div className="absolute w-32 h-32 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-full shadow-lg -bottom-25 left-1/2 md:h-40 md:w-40">
          <Image
            className="absolute hidden transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 md:block"
            src={`/images/subpage/${platform.hero_center_logo}`}
            width={100}
            height={150}
            alt="Hero Center"
            priority
          />
          <Image
            className="absolute block transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 md:hidden"
            src={`/images/subpage/${platform.hero_center_logo}`}
            width={70}
            height={100}
            alt="Hero Center"
            priority
          />
        </div>
      </section>
    );
  };

  const renderPriceSection = () => {
    return (
      <section className="pt-32 preise" id="preise">
        <div className="py-8 px-8 lg:pl-8 mx-auto sm:px-8 max-w-7xl sm:py-12 lg:py-16">
          <div className="text-center">
            <h2>{t.price}</h2>
            <p className="text-lg pt-6">{t.price_description}</p>
          </div>
          {platform.category === "Google Loschen" || platform.category === "Jameda Loschen" ? (
            <div className="mx-auto flex h-full lg:items-end justify-between lg:flex-row flex-col gap-12 md:items-center">
              {packages[platform.category]?.map((data, index) => (
                <div key={index} className="lg:mt-10 mx-auto flex h-full lg:items-end justify-between lg:flex-row flex-col gap-6 lg:gap-12 md:items-center">
                  <div className="lg:w-6/12 h-full flex flex-col gap-4 lg:gap-10 pt-8">
                    <h4 className="text-4xl font-semibold">{data.titleleft}</h4>
                    <p>{data.leftdesc1}</p>
                    <p>{data.leftdesc2}</p>
                    <div>
                      <div className="bg-white rounded-3xl px-10 py-8 hidden lg:block" style={{ boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px" }}>
                        <div>
                          <label htmlFor="customRange2" className="mb-3 inline-block font-semibold">{data.rangeTitle}</label>
                          {renderRangeInput(platform.category, value, handleChange)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="lg:w-4/12 hidden lg:block">
                    <div className="preise-item-inner relative mx-auto hover:cursor-pointer group bg-gradient-to-l from-blue-900 to-blue-600 text-white transition-all md:w-full py-10 px-8 xl:px-14 max-w-md border rounded-3xl flex flex-col text-center items-center justify-start gap-4">
                      <h4 className="font-bold uppercase">{data.title}</h4>
                      <div className="flex items-start font-bold preise-item-inner-price group-hover:text-white !text-white">
                        <span className="text-4xl xl:text-6xl preise-item-inner-price-value">{price}</span>
                        <span className="text-3xl preise-item-inner-price-currency">€</span>
                      </div>
                      <div className="flex items-center justify-center preise-item-inner-pro-bewertung">
                        <p className="preise-item-inner-pro-bewertung-value group-hover:text-white !text-white">
                          <strong id="pro-bewertung">{proBewertung}</strong>{" "}
                          {proBewertung === 1 && data.name === "Bewertungen" ? "Bewertung" : data.name}
                        </p>
                      </div>
                      <p className="preise-item-inner-total relative -top-2 group-hover:text-white !text-white">{data.garantie}</p>
                      <div className="p-4 preise-item-inner-button">
                        <a
                          onClick={() => {
                            setIsSelected(true);
                            setSelectedPlatform(platform.category);
                            setPriceLoschen(price);
                          }}
                          className="rounded-full font-medium py-3.5 px-6 sm:px-12 group-hover:bg-white group-hover:text-blue-700 text-sm group-hover:border-blue-700 !text-blue-700 bg-white"
                          href="#kontakt"
                        >
                          Jetzt Buchen
                        </a>
                      </div>
                      <ul className="space-y-3 pb-2 text-sm">
                        {data.features.map((feature, index) => (
                          <div key={index} dangerouslySetInnerHTML={{ __html: feature }} className="group-hover:text-white text-xs sm:text-sm active:text-white text-left !text-white"></div>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Swiper className="w-full mx-auto" spaceBetween={50} slidesPerView={3} breakpoints={{
              0: { slidesPerView: 1.14, spaceBetween: 20 },
              600: { slidesPerView: 1.6 },
              991: { slidesPerView: 2.6 },
              1200: { slidesPerView: 3 }
            }}>
              {packages[platform.category]?.map((data, index) => (
                <SwiperSlide key={index} className="mx-auto preise-inner-content">
                  <PriceCard
                    title={data.title}
                    price={data.price}
                    value={data.value}
                    proBewertung={data.proBewertung}
                    total={Math.round(data.proBewertung * parseFloat(data.price.replace(",", ".")) * 100) / 100}
                    features={data.features}
                    active={(index === 1 && !isMobile) || isMobile}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </div>
      </section>
    );
  };

  const renderStep1Section = () => {
    return (
      <section className="step-1">
        <div className="px-8 py-8 mx-auto max-w-7xl sm:py-12 lg:py-16">
          <div className="grid items-center grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-8 place-content-center">
            <div className="relative overflow-hidden">
              <video autoPlay loop muted playsInline poster={`/images/subpage/${platform.step_1_image}`}>
                {platform.category === "Jameda Loschen" ? (
                  <source src="/images/subpage/jameda/video.mp4" type="video/mp4" />
                ) : (
                  <source src="/video/ranking.mp4" type="video/mp4" />
                )}
              </video>
            </div>
            <div className="w-full max-w-md mx-auto lg:py-24">
              {platform.category.includes("Google Loschen") ? (
                <>
                  <h2>{t.dynamic_page_loeschung_step1_title}</h2>
                  <p className="mt-6">{t.dynamic_page_loeschung_step1_description}</p>
                </>
              ) :
                platform.category.includes("Jameda") ? (
                  <>
                    <h2>{t.dynamic_page_loeschung_jameda_step1_title}</h2>
                    <p className="mt-6">{t.dynamic_page_loeschung_jameda_step1_description}</p>
                  </>
                ) : (
                  <>
                    <h2>{t.dynamic_page_step1_title.replace(/@@burasidegisecek@@/g, platform.category)}</h2>
                    <div className="mt-6" dangerouslySetInnerHTML={{ __html: t.dynamic_page_step1_description.replace(/@@burasidegisecek@@/g, platform.category) }}></div>
                  </>
                )}
            </div>
          </div>
        </div>
      </section>
    )
  };

  const renderStep2Section = () => {
    return (
      <section className="step-2">
        <div className="px-8 py-8 mx-auto max-w-7xl sm:py-12 lg:py-16">
          <div className="grid items-center grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-8 place-content-center">
            <div className="relative overflow-hidden lg:order-last">
              <Image src={`/images/subpage/${platform.step_2_image}`} width={640} height={646} className="mx-auto" alt={`${platform.category} Step-2`} />
            </div>
            <div className="w-full max-w-md mx-auto lg:py-24">
              {platform.category.includes("Google Loschen") ? (
                <>
                  <h2>{t.dynamic_page_loeschung_step2_title}</h2>
                  <p className="mt-6">{t.dynamic_page_loeschung_step2_description}</p>
                </>
              ) :
                platform.category.includes("Jameda") ? (
                  <>
                    <h2>{t.dynamic_page_loeschung_jameda_step2_title}</h2>
                    <p className="mt-6">{t.dynamic_page_loeschung_jameda_step2_description}</p>
                  </>
                ) :
                  (
                    <>
                      <h2>{t.dynamic_page_step2_title.replace(/@@burasidegisecek@@/g, platform.category)}</h2>
                      <p className="mt-6" dangerouslySetInnerHTML={{ __html: t.dynamic_page_step2_description.replace(/@@burasidegisecek@@/g, platform.category) }}></p>
                    </>
                  )}
            </div>
          </div>
        </div>
      </section>
    )
  }

  const renderStep3Section = () => {
    return (
      <section className="step-3">
        <div className="px-8 py-8 mx-auto max-w-7xl sm:py-12 lg:py-16">
          <div className="grid items-center grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-8 place-content-center">
            <div className="relative overflow-hidden">
              <video autoPlay loop muted playsInline>
                <source src="/video/only-comments.mp4" type="video/mp4" />
              </video>
            </div>
            <div className="w-full max-w-md mx-auto lg:py-24">
              <h2>{t.dynamic_page_step3_title.replace(/@@burasidegisecek@@/g, platform.category)}</h2>
              <p className="mt-6">{t.dynamic_page_step3_description.replace(/@@burasidegisecek@@/g, platform.category)}</p>
            </div>
          </div>
        </div>
      </section>
    )
  }


  return (
    <>
      <Head>
        <title>Funfsterne - {platform.category}</title>
      </Head>
      <div>
        {isLoading ? (
          <div className="fixed z-50 flex items-center justify-center w-full h-full bg-[#fcfcff]">
            <Image src={"/images/global/preloader.gif"} width={400} height={400} alt={"Preloader Gif"} />
          </div>
        ) : null}
        <div className={isLoading ? "hidden" : ""}>
          {renderHeroSection()}
          {renderPriceSection()}
          {renderStep1Section()}
          {renderStep2Section()}
          {platform.category !== "Google Loschen" && platform.category !== "Jameda Loschen" && (
            renderStep3Section()
          )}
          <Services />
        </div>
      </div>
    </>
  );
}

export default PlatformPage;

export async function getStaticPaths() {
  const locales = ["de", "en", "es", "it", "fr"];
  const paths = [];

  locales.forEach((locale) => {
    const filePath = path.join(process.cwd(), `locales/${locale}/platforms.json`);
    const fileContent = fs.readFileSync(filePath, "utf8");
    const platforms = JSON.parse(fileContent);

    platforms.forEach((platform) => {
      paths.push({ params: { slug: platform.slug }, locale });
    });
  });

  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps({ params, locale }) {
  const filePath = path.join(process.cwd(), `locales/${locale}/platforms.json`);
  const fileContent = fs.readFileSync(filePath, "utf8");
  const platforms = JSON.parse(fileContent);
  const platform = platforms.find((p) => p.slug === params.slug);
  return {
    props: {
      platform,
    },
  };
}